import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, Maximize2, Minimize2, RotateCcw, ShieldCheck, Coins, Flame } from "lucide-react";
import { toast } from "sonner";
import { ptrClaim, streakCheckin, trackLaunch } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * In-page PWA launcher. Renders the app in an iframe overlay so users
 * never leave Jixxeo. `url` can be a same-origin path like /embed/vibes
 * or a full external URL.
 */
export default function PwaLauncher({ open, onClose, app }) {
  const { user, refresh } = useAuth();
  const [full, setFull] = useState(false);
  const [reloads, setReloads] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [claimed60, setClaimed60] = useState(false);
  const [claimed300, setClaimed300] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Play-time tracker
  useEffect(() => {
    if (!open) return;
    trackLaunch(app?.slug);
    setElapsed(0); setClaimed60(false); setClaimed300(false);
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [open, app?.slug]);

  // Daily streak check-in — fires once per app-launch overlay open
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await streakCheckin();
        if (cancelled) return;
        if (!r.already_claimed && r.reward > 0) {
          toast.success(`Day ${r.current_streak} streak! +${r.reward} coins`, {
            description: r.longest_streak > r.current_streak
              ? `Longest: ${r.longest_streak} days`
              : "Keep the fire burning tomorrow",
          });
          refresh();
        }
      } catch {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id]);

  useEffect(() => {
    if (!open || !user) return;
    const claim = async (secs) => {
      try {
        const r = await ptrClaim(app.slug, secs);
        if (r.ok && !r.already_claimed && r.reward > 0) {
          toast.success(`+${r.reward} coins earned!`, { description: `Play-time reward · ${Math.floor(secs / 60)} min` });
          refresh();
        }
      } catch {}
    };
    if (elapsed >= 60 && !claimed60) { setClaimed60(true); claim(60); }
    if (elapsed >= 300 && !claimed300) { setClaimed300(true); claim(300); }
  }, [elapsed, open, user, app, claimed60, claimed300, refresh]);

  if (!open || !app) return null;
  const url = app.launch_url;
  const isSameOrigin = url && url.startsWith("/");
  const src = isSameOrigin ? url : url;
  const openExternally = () => window.open(url, "_blank", "noopener");

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-0 md:p-6"
           onClick={onClose}>
        <motion.div
          initial={{ scale: 0.98, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}
          className={`relative w-full ${full ? "h-full max-w-none" : "max-w-6xl h-[100dvh] md:h-[90vh]"} bg-[#050505] md:rounded-3xl overflow-hidden border border-white/10 flex flex-col`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Chrome */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 glass-strong">
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#FF003C]/15 hover:bg-[#FF003C]/30 text-[#FF003C] flex items-center justify-center flex-shrink-0" title="Close">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              {app.icon_url && <img src={app.icon_url} alt="" className="w-6 h-6 rounded" />}
              <div className="min-w-0">
                <div className="font-heading font-bold text-sm truncate">{app.name}</div>
                <div className="mono text-[9px] uppercase tracking-widest text-white/50 truncate">{app.developer}</div>
              </div>
            </div>
            <div className="ml-auto hidden md:flex items-center gap-2">
              {user && (
                <div className="mono text-[10px] uppercase tracking-widest bg-[#FFE300]/10 border border-[#FFE300]/30 text-[#FFE300] rounded-full px-2.5 py-1 flex items-center gap-1">
                  <Coins className="w-3 h-3" />
                  {elapsed < 60 ? `${60 - elapsed}s → +3` : elapsed < 300 ? `${Math.ceil((300 - elapsed) / 60)}m → +10` : "Max earned"}
                </div>
              )}
              <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-2.5 py-1 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3" /> Running instantly in Jixxeo
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => { setReloads((r) => r + 1); if (iframeRef.current) iframeRef.current.src = iframeRef.current.src; }}
                className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white" title="Reload">
                <RotateCcw className="w-4 h-4" />
              </button>
              <button onClick={() => setFull((f) => !f)}
                className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white" title="Toggle fullscreen">
                {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button onClick={openExternally}
                className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white" title="Open in new tab">
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Iframe */}
          <div className="flex-1 bg-black relative">
            {url ? (
              <iframe
                key={reloads}
                ref={iframeRef}
                src={src}
                title={app.name}
                className="w-full h-full border-0"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads allow-pointer-lock allow-orientation-lock"
                allow="autoplay; clipboard-read; clipboard-write; camera; microphone; fullscreen; geolocation; display-capture; screen-wake-lock; presentation; midi; gamepad; xr-spatial-tracking"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-white/60 mono text-sm">
                No launch URL configured.
              </div>
            )}
            {/* Embedding hint removed for uninterrupted member experience */}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
